import React, { useState } from "react";
import { MolecularBg } from "../components/MolecularBg";
import { Lock, Check, X, RefreshCw, MessageSquare, Inbox, Package, PackageCheck, Tag, FileText, Settings as SettingsIcon, Heart, MessageCircle, ShieldCheck, Users, Gift, BarChart3, Target, Mail, Home, Activity, Truck, Megaphone, Power, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { AdminProductsPanel } from "../components/AdminProductsPanel";
import { AdminCategoriesPanel } from "../components/AdminCategoriesPanel";
import { AdminOrdersLog } from "../components/AdminOrdersLog";
import { AdminDiscountsPanel } from "../components/AdminDiscountsPanel";
import { AdminSettingsPanel } from "../components/AdminSettingsPanel";
import { AdminCoaPanel } from "../components/AdminCoaPanel";
import { AdminCoasPanel } from "../components/AdminCoasPanel";
import { AdminChatPanel } from "../components/AdminChatPanel";
import { AdminWishlistStats } from "../components/AdminWishlistStats";
import { AdminCustomersPanel } from "../components/AdminCustomersPanel";
import { AdminVouchersPanel } from "../components/AdminVouchersPanel";
import { AdminAnalyticsPanel } from "../components/AdminAnalyticsPanel";
import { AdminSegmentsPanel } from "../components/AdminSegmentsPanel";
import { AdminEmailTemplatesPanel } from "../components/AdminEmailTemplatesPanel";
import { AdminHomepagePanel } from "../components/AdminHomepagePanel";
import { AdminLiveAnalyticsPanel } from "../components/AdminLiveAnalyticsPanel";
import { AdminShippingPanel } from "../components/AdminShippingPanel";
import { AdminAnnouncementsPanel } from "../components/AdminAnnouncementsPanel";
import { AdminPreOrdersPanel } from "../components/AdminPreOrdersPanel";
import { AdminMaintenancePanel } from "../components/AdminMaintenancePanel";
import { AdminPaymentMethodsPanel } from "../components/AdminPaymentMethodsPanel";
import { useAdminOrderNotifier, NewOrderBadge } from "../components/AdminOrderNotifier";

export const AdminPage = () => {
    const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
    const [authed, setAuthed] = useState(!!localStorage.getItem("admin_token"));
    const [tab, setTab] = useState("dashboard");
    const [pendingReviews, setPendingReviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const { unread, clearUnread } = useAdminOrderNotifier();

    const headers = { "X-Admin-Token": token };

    const load = async (t = token) => {
        setLoading(true);
        try {
            const revs = await api.get("/admin/reviews/pending", { headers: { "X-Admin-Token": t } });
            setPendingReviews(revs.data);
        } catch {
            toast.error("Unauthorized or fetch failed");
            setAuthed(false);
            localStorage.removeItem("admin_token");
        }
        setLoading(false);
    };

    const login = (e) => {
        e.preventDefault();
        localStorage.setItem("admin_token", token);
        setAuthed(true);
        load(token);
    };

    React.useEffect(() => {
        if (authed) load(); /* eslint-disable-next-line */
    }, []);

    const moderate = async (id, action) => {
        try {
            await api.post(`/admin/reviews/${id}/${action}`, {}, { headers });
            toast.success(`Review ${action}d`);
            setPendingReviews((p) => p.filter((r) => r.id !== id));
        } catch {
            toast.error("Action failed");
        }
    };

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="admin-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10 max-w-6xl">
                <span className="eyebrow">// Admin</span>
                <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 mb-8">
                    Carbon Labs Admin
                </h1>

                {!authed ? (
                    <form
                        onSubmit={login}
                        className="glass-elevated rounded-2xl p-6 max-w-md neon-border-purple"
                        data-testid="admin-login-form"
                    >
                        <Lock size={24} className="text-[var(--neon-purple)] mb-3" />
                        <h2 className="font-display text-xl font-bold text-white mb-3">Admin Access</h2>
                        <input
                            type="password"
                            required
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="Admin token"
                            className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]"
                            data-testid="admin-token-input"
                        />
                        <button type="submit" className="btn-solid mt-3 w-full" data-testid="admin-login-button">
                            Sign in
                        </button>
                    </form>
                ) : (
                    <>
                        {/* Tabs — wrap on small screens so all options are visible */}
                        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-tabs">
                            <button
                                onClick={() => setTab("dashboard")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "dashboard"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-dashboard"
                            >
                                <BarChart3 size={14} /> Dashboard
                            </button>
                            <button
                                onClick={() => setTab("orders")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "orders"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-orders"
                            >
                                <Inbox size={14} /> Orders
                            </button>
                            <button
                                onClick={() => setTab("preorders")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "preorders"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-preorders"
                            >
                                <PackageCheck size={14} /> Pre-orders
                            </button>
                            <button
                                onClick={() => setTab("reviews")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "reviews"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-reviews"
                            >
                                <MessageSquare size={14} /> Reviews ({pendingReviews.length})
                            </button>
                            <button
                                onClick={() => setTab("products")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "products"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-products"
                            >
                                <Package size={14} /> Products
                            </button>
                            <button
                                onClick={() => setTab("categories")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "categories"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-categories"
                            >
                                <Tag size={14} /> Categories
                            </button>
                            <button
                                onClick={() => setTab("customers")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "customers"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-customers"
                            >
                                <Users size={14} /> Customers
                            </button>
                            <button
                                onClick={() => setTab("vouchers")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "vouchers"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-vouchers"
                            >
                                <Gift size={14} /> Vouchers
                            </button>
                            <button
                                onClick={() => setTab("marketing")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "marketing"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-marketing"
                            >
                                <Target size={14} /> Marketing
                            </button>
                            <button
                                onClick={() => setTab("automation")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "automation"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-automation"
                            >
                                <Mail size={14} /> Automation
                            </button>
                            <button
                                onClick={() => setTab("discounts")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "discounts"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-discounts"
                            >
                                <Tag size={14} /> Discounts
                            </button>
                            <button
                                onClick={() => setTab("chat")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "chat"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-chat"
                            >
                                <MessageCircle size={14} /> Chat
                            </button>
                            <button
                                onClick={() => setTab("coa")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "coa"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-coa"
                            >
                                <FileText size={14} /> COA Requests
                            </button>
                            <button
                                onClick={() => setTab("wishlist")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "wishlist"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-wishlist"
                            >
                                <Heart size={14} /> Wishlist
                            </button>
                            <button
                                onClick={() => setTab("coas")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "coas"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-coas"
                            >
                                <ShieldCheck size={14} /> COA Library
                            </button>
                            <button
                                onClick={() => { setTab("live-analytics"); clearUnread(); }}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "live-analytics"
                                        ? "border-[var(--tiffany)] text-[var(--tiffany)] bg-[rgba(79,209,197,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-live-analytics"
                            >
                                <Activity size={14} /> Live Analytics
                                <NewOrderBadge count={unread} />
                            </button>
                            <button
                                onClick={() => setTab("homepage")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "homepage"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-homepage"
                            >
                                <Home size={14} /> Homepage
                            </button>
                            <button
                                onClick={() => setTab("shipping")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "shipping"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-shipping"
                            >
                                <Truck size={14} /> Shipping
                            </button>
                            <button
                                onClick={() => setTab("announcements")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "announcements"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-announcements"
                            >
                                <Megaphone size={14} /> Announcements
                            </button>
                            <button
                                onClick={() => setTab("maintenance")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "maintenance"
                                        ? "border-red-500/60 text-red-300 bg-red-500/10"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-maintenance"
                            >
                                <Power size={14} /> Maintenance
                            </button>
                            <button
                                onClick={() => setTab("payment-methods")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "payment-methods"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-payment-methods"
                            >
                                <CreditCard size={14} /> Payment Methods
                            </button>
                            <button
                                onClick={() => setTab("settings")}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-2 ${
                                    tab === "settings"
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid="tab-settings"
                            >
                                <SettingsIcon size={14} /> Settings
                            </button>
                            <button onClick={() => load()} className="ml-auto btn-neon" data-testid="admin-refresh">
                                <RefreshCw size={14} /> Refresh
                            </button>
                        </div>

                        {tab === "products" && <AdminProductsPanel token={token} />}

                        {tab === "categories" && <AdminCategoriesPanel token={token} />}

                        {tab === "customers" && <AdminCustomersPanel token={token} />}

                        {tab === "vouchers" && <AdminVouchersPanel token={token} />}

                        {tab === "marketing" && <AdminSegmentsPanel token={token} />}

                        {tab === "automation" && <AdminEmailTemplatesPanel token={token} />}

                        {tab === "discounts" && <AdminDiscountsPanel token={token} />}

                        {tab === "chat" && <AdminChatPanel token={token} />}

                        {tab === "coa" && <AdminCoaPanel token={token} />}

                        {tab === "wishlist" && <AdminWishlistStats token={token} />}

                        {tab === "settings" && <AdminSettingsPanel token={token} />}

                        {tab === "homepage" && <AdminHomepagePanel adminToken={token} />}

                        {tab === "shipping" && <AdminShippingPanel token={token} />}

                        {tab === "announcements" && <AdminAnnouncementsPanel token={token} />}

                        {tab === "live-analytics" && <AdminLiveAnalyticsPanel />}

                        {tab === "coas" && <AdminCoasPanel token={token} />}

                        {tab === "dashboard" && <AdminAnalyticsPanel token={token} />}

                        {tab === "orders" && <AdminOrdersLog token={token} />}

                        {tab === "preorders" && <AdminPreOrdersPanel token={token} />}

                        {tab === "maintenance" && <AdminMaintenancePanel token={token} />}

                        {tab === "payment-methods" && <AdminPaymentMethodsPanel token={token} />}

                        {tab === "reviews" && (
                            <div className="space-y-3" data-testid="pending-reviews-list">
                                {pendingReviews.length === 0 && !loading && (
                                    <div className="glass rounded-2xl p-8 text-center text-[var(--text-secondary)]">
                                        No pending reviews.
                                    </div>
                                )}
                                {pendingReviews.map((r) => (
                                    <div
                                        key={r.id}
                                        className="glass-elevated rounded-2xl p-5"
                                        data-testid={`pending-review-${r.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="flex-1 min-w-[200px]">
                                                <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">
                                                    {r.product_slug} · {r.customer_name} · {r.rating}★
                                                </div>
                                                <h4 className="font-display font-bold text-white">{r.title}</h4>
                                                <p className="text-sm text-[var(--text-secondary)] mt-2">{r.body}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => moderate(r.id, "approve")}
                                                    className="px-3 py-2 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                                    data-testid={`approve-${r.id}`}
                                                >
                                                    <Check size={12} /> Approve
                                                </button>
                                                <button
                                                    onClick={() => moderate(r.id, "reject")}
                                                    className="px-3 py-2 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                                    data-testid={`reject-${r.id}`}
                                                >
                                                    <X size={12} /> Reject
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
